/*
  Wash Day Set — global cart guard
  --------------------------------
  Add this file to Shopify:
    Online Store > Themes > Edit code > Assets
    Create: wash-day-bundle-guard.js

  Then load it globally from layout/theme.liquid:
    <script src="{{ 'wash-day-bundle-guard.js' | asset_url }}" defer="defer"></script>

  PURPOSE
  -------
  A Wash Day Set is valid only while ALL FOUR marked bundle components exist:
    duo + cleanser + cream + sachets

  Complete marked bundle:
    WASHDAY-CLEANSER
    WASHDAY-CREAM
    WASHDAY-SACHETS
  stay/applied.

  Broken marked bundle:
    all Wash Day discount codes are removed together.

  Normal copies of these products do NOT count because the product snippet
  marks bundle lines with:
    _wash_day_set = 1
    _wash_day_component = duo|cleanser|cream|sachets
*/

(function() {
  'use strict';

  if (window.__washDayBundleGuardInstalled) return;
  window.__washDayBundleGuardInstalled = true;

  /* 15 Oct: one Buy X Get Y code (needs the Revive Duo + all three gifts at checkout).
     The old per-item codes are still recognised so they get cleared from old carts. */
  const WASH_DAY_CODES = [
    'WASHDAY-GIFTS'
  ];
  const LEGACY_WASH_DAY_CODES = [
    'WASHDAY-CLEANSER',
    'WASHDAY-CREAM',
    'WASHDAY-SACHETS'
  ];

  const REQUIRED_COMPONENTS = [
    'duo',
    'cleanser',
    'cream',
    'sachets'
  ];

  const BUNDLE_MARKER_KEY = '_wash_day_set';
  const COMPONENT_KEY = '_wash_day_component';
  const SESSION_FLAG = '__washDayBundleDiscountsApplied';

  /*
    Preserve whatever fetch implementation existed before this guard.
    All of the guard's own Ajax requests use this saved function so they do
    not recursively trigger the fetch watcher below.
  */
  const nativeFetch = window.fetch.bind(window);

  let syncing = false;
  let scheduledTimer = null;
  let scheduledRefresh = false;

  function shopRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) || '/';
  }

  function normaliseCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function isWashDayCode(value) {
    const code = normaliseCode(value);

    return WASH_DAY_CODES.concat(LEGACY_WASH_DAY_CODES).some(function(washDayCode) {
      return normaliseCode(washDayCode) === code;
    });
  }

  function getSessionFlag() {
    try {
      return sessionStorage.getItem(SESSION_FLAG) === '1';
    } catch (error) {
      return false;
    }
  }

  function setSessionFlag(active) {
    try {
      if (active) {
        sessionStorage.setItem(SESSION_FLAG, '1');
      } else {
        sessionStorage.removeItem(SESSION_FLAG);
      }
    } catch (error) {
      /* Storage can be unavailable in hardened/private browser modes. */
    }
  }

  async function getCart() {
    const response = await nativeFetch(shopRoot() + 'cart.js', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Unable to read cart for Wash Day Set validation.');
    }

    return response.json();
  }

  function getBundleState(cart) {
    const quantitiesByRole = Object.create(null);

    (cart.items || []).forEach(function(item) {
      const properties = item && item.properties ? item.properties : {};

      if (String(properties[BUNDLE_MARKER_KEY] || '') !== '1') return;

      const role = String(properties[COMPONENT_KEY] || '').trim().toLowerCase();
      if (!role) return;

      const quantity = Number(item.quantity) || 0;

      quantitiesByRole[role] =
        (quantitiesByRole[role] || 0) + quantity;
    });

    const complete = REQUIRED_COMPONENTS.every(function(role) {
      return (quantitiesByRole[role] || 0) > 0;
    });

    const hasAnyMarkedComponent = Object.keys(quantitiesByRole).some(function(role) {
      return (quantitiesByRole[role] || 0) > 0;
    });

    return {
      complete: complete,
      hasAnyMarkedComponent: hasAnyMarkedComponent,
      quantitiesByRole: quantitiesByRole
    };
  }

  /*
    Shopify exposes discount applications on cart lines/cart totals.
    For discount-code applications, the title is the customer-facing code
    name. We use that to preserve OTHER active discount codes while removing
    only this bundle's codes.
  */
  function getActiveDiscountCodes(cart) {
    const found = new Map();

    function inspectApplication(application) {
      if (!application) return;

      const type = String(application.type || '').toLowerCase();
      const title = String(application.title || '').trim();

      if (type !== 'discount_code' || !title) return;

      found.set(normaliseCode(title), title);
    }

    (cart.cart_level_discount_applications || []).forEach(
      inspectApplication
    );

    (cart.items || []).forEach(function(item) {
      (item.line_level_discount_allocations || []).forEach(function(allocation) {
        inspectApplication(
          allocation &&
          (
            allocation.discount_application ||
            allocation.discountApplication
          )
        );
      });
    });

    return Array.from(found.values());
  }

  function getNonWashDayCodes(cart) {
    return getActiveDiscountCodes(cart).filter(function(code) {
      return !isWashDayCode(code);
    });
  }

  function getActiveWashDayCodes(cart) {
    return getActiveDiscountCodes(cart).filter(function(code) {
      return isWashDayCode(code);
    });
  }

  function getOpenCartRenderer() {
    const drawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    if (!drawer) return null;

    const visiblyOpen =
      drawer.classList.contains('active') ||
      drawer.hasAttribute('open') ||
      drawer.getAttribute('aria-hidden') === 'false' ||
      !!drawer.querySelector('details[open]');

    return visiblyOpen ? drawer : null;
  }

  function getRendererSections(renderer) {
    if (
      !renderer ||
      typeof renderer.getSectionsToRender !== 'function'
    ) {
      return [];
    }

    return renderer
      .getSectionsToRender()
      .map(function(section) {
        return section && section.id ? section.id : null;
      })
      .filter(Boolean);
  }

  function isCartPage() {
    const pathname = String(window.location.pathname || '');
    const root = shopRoot().replace(/\/$/, '');

    return pathname === root + '/cart' || pathname === '/cart';
  }

  async function updateDiscountCodes(codes, cartBeforeUpdate, refreshUi) {
    const renderer = refreshUi ? getOpenCartRenderer() : null;
    const sections = getRendererSections(renderer);

    const body = {
      /*
        Shopify accepts multiple codes as a comma-separated value.
        An empty string removes the currently applied discount codes.
      */
      discount: codes.join(',')
    };

    if (sections.length) {
      body.sections = sections;
      body.sections_url =
        window.location.pathname + window.location.search;
    }

    const response = await nativeFetch(shopRoot() + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Unable to update Wash Day Set discounts.'
      );
    }

    if (
      renderer &&
      data.sections &&
      typeof renderer.renderContents === 'function'
    ) {
      if (!data.id) {
        const firstItem =
          (cartBeforeUpdate && cartBeforeUpdate.items && cartBeforeUpdate.items[0]) ||
          (data.items && data.items[0]);

        if (firstItem) {
          data.id = firstItem.id || firstItem.variant_id;
        }
      }

      if (typeof renderer.setActiveElement === 'function') {
        renderer.setActiveElement(document.activeElement);
      }

      renderer.renderContents(data);
    } else if (refreshUi && isCartPage()) {
      /*
        The cart page's exact section renderer differs between themes.
        A single reload here is safer than leaving stale free prices visible.
      */
      window.setTimeout(function() {
        window.location.reload();
      }, 40);
    } else {
      /*
        Let custom theme/app scripts optionally react without forcing the
        drawer to open on normal page load.
      */
      window.dispatchEvent(
        new CustomEvent('washday:discounts-updated', {
          detail: {
            codes: codes.slice(),
            cart: data
          }
        })
      );
    }

    return data;
  }

  async function validateBundle(options) {
    const refreshUi = !!(options && options.refreshUi);

    if (syncing) return;

    /*
      The product snippet is currently doing:
        add 4 items -> apply 3 codes -> render drawer.
      Do not race that transaction.
    */
    if (window.__washDayBundleBusy) {
      scheduleValidation(refreshUi, 120);
      return;
    }

    syncing = true;

    try {
      const cart = await getCart();
      const state = getBundleState(cart);

      const activeWashDayCodes = getActiveWashDayCodes(cart);
      const nonWashDayCodes = getNonWashDayCodes(cart);
      const sessionSaysApplied = getSessionFlag();

      if (state.complete) {
        /*
          Complete marked set:
          make sure all three Wash Day codes are present, while preserving
          other ACTIVE discount codes that Shopify exposes on this cart.
        */
        const activeNormalised = new Set(
          activeWashDayCodes.map(normaliseCode)
        );

        const missingWashDayCode = WASH_DAY_CODES.some(function(code) {
          return !activeNormalised.has(normaliseCode(code));
        });

        if (missingWashDayCode) {
          await updateDiscountCodes(
            nonWashDayCodes.concat(WASH_DAY_CODES),
            cart,
            refreshUi
          );
        }

        setSessionFlag(true);
        return;
      }

      /*
        Broken set — even if only one marked component is missing.

        The session flag matters because after an eligible product is removed,
        its discount application can disappear from /cart.js even though the
        code was previously applied to the cart.
      */
      const shouldRemoveWashDayCodes =
        activeWashDayCodes.length > 0 ||
        sessionSaysApplied;

      if (shouldRemoveWashDayCodes) {
        await updateDiscountCodes(
          nonWashDayCodes,
          cart,
          refreshUi
        );
      }

      setSessionFlag(false);

    } catch (error) {
      console.error('[Wash Day Set Guard]', error);
    } finally {
      syncing = false;
    }
  }

  function scheduleValidation(refreshUi, delay) {
    scheduledRefresh = scheduledRefresh || !!refreshUi;

    if (scheduledTimer) {
      window.clearTimeout(scheduledTimer);
    }

    scheduledTimer = window.setTimeout(function() {
      const shouldRefresh = scheduledRefresh;

      scheduledTimer = null;
      scheduledRefresh = false;

      validateBundle({
        refreshUi: shouldRefresh
      });
    }, typeof delay === 'number' ? delay : 90);
  }

  function isCartMutationRequest(input, init) {
    try {
      const rawUrl =
        typeof input === 'string'
          ? input
          : input && input.url
            ? input.url
            : '';

      if (!rawUrl) return false;

      const url = new URL(rawUrl, window.location.origin);
      const method = String(
        (init && init.method) ||
        (input && input.method) ||
        'GET'
      ).toUpperCase();

      if (method === 'GET') return false;

      return /\/cart\/(?:add|change|update|clear)(?:\.js)?$/.test(
        url.pathname
      );
    } catch (error) {
      return false;
    }
  }

  /*
    Watch ALL theme/app Ajax cart mutations.

    The returned Response is not changed. After the original cart request
    completes, the guard verifies the marked bundle.
  */
  window.fetch = async function(input, init) {
    const response = await nativeFetch(input, init);

    if (
      isCartMutationRequest(input, init) &&
      !window.__washDayBundleBusy
    ) {
      scheduleValidation(true, 100);
    }

    return response;
  };

  /*
    The product snippet explicitly dispatches this after its own transaction.
  */
  window.addEventListener('washday:cart-changed', function() {
    scheduleValidation(true, 100);
  });

  /*
    Validate on every page load / bfcache restore. This covers navigation to
    /cart or another page after a bundle was changed elsewhere.
  */
  window.addEventListener('pageshow', function() {
    scheduleValidation(false, 60);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      scheduleValidation(false, 60);
    }, { once: true });
  } else {
    scheduleValidation(false, 60);
  }
})();
